import { create } from "zustand";

import {
  MachineSalesDailyBucket,
  MachineSalesBreakdown,
  fetchMachineCollectionSummaryBulk,
  fetchMachineSalesSnapshot,
  fetchMachineSalesTransactionPage,
  MachineSalesTotals,
  MachineSalesWindow,
  syncMachineSales,
} from "../api/machine/api";
import { MachineCollectionSummary, MachineSale, SalesComparisonPeriod } from "../types";
import {
  liveSalesPerfRangeLabel,
  logLiveSalesResourceTiming,
  measureLiveSalesStep,
  measureLiveSalesSync,
} from "../liveSalesPerf";

const bucketKey = (bucket: Pick<MachineSalesDailyBucket, "machine" | "location" | "date">) =>
  `${bucket.machine}:${bucket.location || "none"}:${bucket.date}`;

type LiveSalesState = {
  salesById: Record<string, MachineSale>;
  dailyBuckets: Record<string, MachineSalesDailyBucket>;
  machineVersions: Record<string, number>;
  collectionSummaries: Record<string, MachineCollectionSummary>;
  collectionSummaryStatus: Record<string, CollectionSummaryRequestStatus>;
  summaryQueries: Record<string, LiveSalesSummaryQuery>;
  transactionQueries: Record<string, LiveSalesTransactionQuery>;
  applySync: (result: Awaited<ReturnType<typeof syncMachineSales>>) => void;
  synchronize: (
    machineIds: string[],
    options?: { forceFollowUp?: boolean }
  ) => Promise<void>;
  synchronizeActiveQueries: () => Promise<void>;
  refreshCalendarQueries: () => Promise<void>;
  loadSummary: (machineIds: string[], start?: string, end?: string, calendar?: LiveSalesCalendar, force?: boolean) => Promise<void>;
  loadTransactions: (request: LiveSalesTransactionRequest, forceFirstPage?: boolean) => Promise<void>;
  loadCollectionSummaries: (machineIds: string[]) => Promise<void>;
  invalidateCollectionSummaries: (machineIds?: string[]) => void;
  reset: () => void;
};

export type CollectionSummaryRequestStatus = {
  loading: boolean;
  error: string | null;
  attempt: number;
};

const COLLECTION_SUMMARY_TIMEOUT_MS = 15000;
let synchronization: Promise<void> | null = null;
let storeGeneration = 0;
const queuedMachineIds = new Set<string>();
const activeSynchronizationMachineIds = new Set<string>();
const summaryRequests = new Map<string, Promise<void>>();
const transactionRequests = new Map<string, Promise<void>>();
const collectionSummaryRequests = new Map<string, Promise<void>>();
const collectionSummaryControllers = new Set<AbortController>();
const transactionControllers = new Set<AbortController>();
const activeSummaryQueries = new Map<string, number>();
const activeTransactionQueries = new Map<string, number>();
const TRANSACTION_REQUEST_TIMEOUT_MS = 8_000;
const TRANSACTION_PAGE_SIZE = 25;

const retainQuery = (queries: Map<string, number>, key: string) => {
  queries.set(key, (queries.get(key) || 0) + 1);
  return () => {
    const remaining = (queries.get(key) || 1) - 1;
    if (remaining > 0) queries.set(key, remaining);
    else queries.delete(key);
  };
};

export const retainLiveSalesSummaryQuery = (key: string) =>
  retainQuery(activeSummaryQueries, key);

export const retainLiveSalesTransactionQuery = (key: string) =>
  retainQuery(activeTransactionQueries, key);

export type LiveSalesTransactionRequest = {
  machineIds: string[];
  start?: string;
  end?: string;
  calendar?: LiveSalesCalendar;
  expenseCalculationId?: string;
  includeTotals?: boolean;
  windows?: MachineSalesWindow[];
};

export type LiveSalesTransactionQuery = LiveSalesTransactionRequest & {
  saleIds: string[];
  nextCursor: string | null;
  hasMore: boolean;
  ready: boolean;
  totals: MachineSalesTotals;
  breakdowns: MachineSalesBreakdown[];
  previousGrossCents?: number | null;
  previousPeriods?: SalesComparisonPeriod[];
  requiresFirstPage?: boolean;
  error?: string | null;
  attempt?: number;
  status?: "idle" | "loading" | "ready" | "refreshing" | "error";
  updatedAt?: number | null;
};

export type LiveSalesCalendar = {
  range?: string;
  startDate?: string;
  endDate?: string;
  locationIds?: string[];
};

export type LiveSalesSummaryQuery = {
  machineIds: string[];
  start?: string;
  end?: string;
  bucketKeys: string[];
  calendar?: LiveSalesCalendar;
  ready: boolean;
  error?: string | null;
  attempt?: number;
};

export const normalizeLiveSalesCalendar = (calendar?: LiveSalesCalendar) =>
  calendar
    ? {
        range: calendar.range || undefined,
        startDate: calendar.startDate || undefined,
        endDate: calendar.endDate || undefined,
        locationIds: Array.from(new Set((calendar.locationIds || []).filter(Boolean))).sort(),
      }
    : undefined;

export const liveSalesSummaryKey = (
  machineIds: string[], start?: string, end?: string, calendar?: LiveSalesCalendar
) => {
  const normalized = normalizeLiveSalesCalendar(calendar);
  return `${Array.from(new Set(machineIds.filter(Boolean))).sort().join("|")}__${start || "calendar"}__${end || "calendar"}__${normalized?.range || "utc"}__${normalized?.startDate || ""}__${normalized?.endDate || ""}__${(normalized?.locationIds || []).join("|")}`;
};
export const liveSalesTransactionsKey = ({
  machineIds, start, end, calendar, expenseCalculationId, includeTotals = true, windows,
}: LiveSalesTransactionRequest) => {
  const windowKey = windows?.length ? JSON.stringify([...windows]
    .sort((a, b) => a.machineId.localeCompare(b.machineId))
    .map((window) => ({ ...window, end: window.live ? "live" : window.end }))) : "";
  return `${liveSalesSummaryKey(machineIds, start, end, calendar)}__expense:${expenseCalculationId || ""}__totals:${includeTotals ? "yes" : "no"}:v4${windowKey ? `__windows:${windowKey}` : ""}`;
};

const saleMatchesQuery = (sale: MachineSale, query: LiveSalesTransactionQuery) => {
  // Calendar membership belongs to the backend resolver. Those queries are
  // invalidated and refetched below; do not duplicate timezone logic here.
  if (query.calendar) return false;
  const window = query.windows?.find((window) => window.machineId === sale.machine);
  const start = window?.start || query.start;
  const end = window?.end || query.end;
  if (!start || !end) return false;
  const timestamp = sale.timestamp ? Date.parse(sale.timestamp) : Number.NaN;
  return Number.isFinite(timestamp) && timestamp >= Date.parse(start) && (window?.live || timestamp < Date.parse(end));
};

const queriesForMachines = <T extends { machineIds: string[] }>(
  queries: Record<string, T>,
  machineIds: Set<string>,
) => Object.entries(queries).filter(([, query]) =>
  query.machineIds.some((machineId) => machineIds.has(machineId)),
);

const withoutMachines = <T>(
  values: Record<string, T>,
  machineIds: Set<string>,
  machineId: (key: string, value: T) => string | undefined,
) => Object.fromEntries(
  Object.entries(values).filter(([key, value]) => {
    const id = machineId(key, value);
    return !id || !machineIds.has(id);
  }),
);

export const useLiveSalesStore = create<LiveSalesState>((set, get) => ({
  salesById: {},
  dailyBuckets: {},
  machineVersions: {},
  collectionSummaries: {},
  collectionSummaryStatus: {},
  summaryQueries: {},
  transactionQueries: {},

  applySync: (result) => {
    set((state) => {
      const salesById = { ...state.salesById };
      const dailyBuckets = { ...state.dailyBuckets };
      const machineVersions = { ...state.machineVersions };
      const collectionSummaries = { ...state.collectionSummaries };
      const transactionQueries = { ...state.transactionQueries };
      result.machines.forEach((machine) => {
        if (machine.version <= (machineVersions[machine.machine] || 0)) return;
        machine.sales.forEach((sale) => {
          if (sale.id) salesById[sale.id] = sale;
        });
        Object.entries(transactionQueries).forEach(([key, query]) => {
          if (!query.machineIds.includes(machine.machine)) return;
          const saleIds = new Set(query.saleIds);
          machine.sales.forEach((sale) => {
            if (
              sale.id &&
              saleMatchesQuery(sale, query)
            ) {
              saleIds.add(sale.id);
            }
          });
          transactionQueries[key] = {
            ...query,
            saleIds: Array.from(saleIds).sort((left, right) =>
              String(salesById[right]?.timestamp || "").localeCompare(
                String(salesById[left]?.timestamp || "")
              )
            ),
          };
        });
        machine.summaryBuckets.forEach((bucket) => {
          dailyBuckets[bucketKey(bucket)] = bucket;
        });
        if (machine.collectionSummary) {
          collectionSummaries[machine.machine] = machine.collectionSummary;
        }
        machineVersions[machine.machine] = machine.version;
      });
      return {
        salesById,
        dailyBuckets,
        machineVersions,
        collectionSummaries,
        transactionQueries,
      };
    });
  },

  synchronize: async (machineIds, options) => {
    machineIds.filter(Boolean).forEach((id) => {
      if (
        options?.forceFollowUp ||
        !activeSynchronizationMachineIds.has(id)
      ) {
        queuedMachineIds.add(id);
      }
    });
    if (synchronization) return synchronization;
    const generation = storeGeneration;
    const request = (async () => {
      while (generation === storeGeneration && queuedMachineIds.size) {
        const ids = Array.from(queuedMachineIds);
        queuedMachineIds.clear();
        ids.forEach((id) => activeSynchronizationMachineIds.add(id));
        const versions = get().machineVersions;
        let result;
        try {
          result = await syncMachineSales(
            ids.map((machineId) => ({
              machineId,
              afterVersion: versions[machineId] ?? null,
            }))
          );
        } finally {
          ids.forEach((id) => activeSynchronizationMachineIds.delete(id));
        }
        if (generation !== storeGeneration) return;
        const resetIds = new Set(
          result.machines.filter((machine) => machine.resetRequired).map((machine) => machine.machine)
        );
        if (resetIds.size) {
          set((state) => ({
            dailyBuckets: withoutMachines(state.dailyBuckets, resetIds, (_key, bucket) => bucket.machine),
            machineVersions: withoutMachines(state.machineVersions, resetIds, (id) => id),
            collectionSummaries: withoutMachines(state.collectionSummaries, resetIds, (id) => id),
          }));
        }
        get().applySync({ machines: result.machines.filter((machine) => !machine.resetRequired) });
        const changedIds = new Set(result.machines.map((machine) => machine.machine));
        // Summary bucket membership is resolved by the backend using each
        // attribution timezone. Refetch active summaries rather than duplicate
        // that calendar logic in the client.
        const affectedQueries = Object.entries(get().summaryQueries).filter(([key, query]) =>
          query.machineIds.some(id => changedIds.has(id)) ||
          (activeSummaryQueries.has(key) && Boolean(query.error)),
        );
        const affectedTransactionQueries = queriesForMachines(
          get().transactionQueries,
          changedIds,
        ).filter(([, query]) =>
          Boolean(query.calendar) ||
          Boolean(query.windows?.some((window) => !window.live)) ||
          query.machineIds.some((id) => resetIds.has(id)),
        );
        // A failed page refresh can follow an already-applied machine cursor.
        // Retry those pages even when the next delta reports no new sales.
        Object.entries(get().transactionQueries).forEach(([key, query]) => {
          if (activeTransactionQueries.has(key) && query.requiresFirstPage &&
              !affectedTransactionQueries.some(([affectedKey]) => affectedKey === key)) {
            affectedTransactionQueries.push([key, query]);
          }
        });
        await refreshLiveSalesQueries(affectedQueries, affectedTransactionQueries);
        if (resetIds.size) {
          await get().loadCollectionSummaries(Array.from(resetIds));
        }
      }
    })();
    synchronization = request;
    try {
      await request;
    } finally {
      if (synchronization === request) synchronization = null;
    }
  },

  synchronizeActiveQueries: () => {
    const state = get();
    const machineIds = new Set<string>();
    activeSummaryQueries.forEach((_count, key) => {
      state.summaryQueries[key]?.machineIds.forEach((id) => machineIds.add(id));
    });
    activeTransactionQueries.forEach((_count, key) => {
      state.transactionQueries[key]?.machineIds.forEach((id) => machineIds.add(id));
    });
    // Refocus can happen on a page without sales widgets. Dormant caches must
    // catch up when reopened, without fetching every inactive range now.
    return Promise.all([
      refreshLiveSalesQueries(
        Object.entries(state.summaryQueries).filter(([key]) => !activeSummaryQueries.has(key)),
        Object.entries(state.transactionQueries).filter(([key]) => !activeTransactionQueries.has(key)),
      ),
      ...(machineIds.size ? [state.synchronize(Array.from(machineIds).sort())] : []),
    ]).then(() => undefined);
  },

  refreshCalendarQueries: () => {
    const state = get();
    return refreshLiveSalesQueries(
      Object.entries(state.summaryQueries).filter(([, query]) => Boolean(query.calendar?.range)),
      Object.entries(state.transactionQueries).filter(([, query]) => Boolean(query.calendar?.range)),
    );
  },

  loadSummary: async (machineIds, start, end, calendar, force = false) => {
    calendar = normalizeLiveSalesCalendar(calendar);
    const queryKey = liveSalesSummaryKey(machineIds, start, end, calendar);
    const current = get().summaryQueries[queryKey];
    if (current?.ready && !force) return;
    const existing = summaryRequests.get(queryKey);
    if (existing) return existing;
    const generation = storeGeneration;
    set((state) => ({ summaryQueries: {
      ...state.summaryQueries,
      [queryKey]: {
        machineIds, start, end, bucketKeys: current?.bucketKeys || [], calendar,
        ready: Boolean(current?.ready), error: null, attempt: (current?.attempt || 0) + 1,
      },
    }}));
    const perfRange = liveSalesPerfRangeLabel(calendar);
    const request = measureLiveSalesStep(
      `load summary API ${perfRange}`,
      () => fetchMachineSalesSnapshot(machineIds, start, end, calendar),
    ).then((result) => {
      logLiveSalesResourceTiming(
        `load summary ${perfRange}`,
        "/machine/sale/snapshot",
      );
      if (generation !== storeGeneration) return;
      measureLiveSalesSync(`load summary merge ${perfRange}`, () => set((state) => {
        const dailyBuckets = { ...state.dailyBuckets };
        const machineVersions = { ...state.machineVersions };
        const keys: string[] = [];
        result.machines.forEach((machine) => {
          const currentVersion = machineVersions[machine.machine] || 0;
          machine.summaryBuckets.forEach((bucket) => {
            const key = bucketKey(bucket);
            if (machine.version >= currentVersion || !dailyBuckets[key]) {
              dailyBuckets[key] = bucket;
            }
            keys.push(key);
          });
          if (machine.version >= currentVersion) {
            machineVersions[machine.machine] = machine.version;
          }
        });
        return {
          dailyBuckets,
          machineVersions,
            summaryQueries: {
            ...state.summaryQueries,
              [queryKey]: { machineIds, start, end, bucketKeys: keys, calendar, ready: true, error: null, attempt: (current?.attempt || 0) + 1 },
          },
        };
      }));
    }).catch((error) => {
      if (generation === storeGeneration) set((state) => ({ summaryQueries: {
        ...state.summaryQueries,
        [queryKey]: {
          ...state.summaryQueries[queryKey],
          ready: Boolean(state.summaryQueries[queryKey]?.ready),
          error: String(error),
        },
      }}));
      throw error;
    });
    summaryRequests.set(queryKey, request);
    try {
      await request;
    } finally {
      if (summaryRequests.get(queryKey) === request) summaryRequests.delete(queryKey);
    }
  },

  loadTransactions: async ({
    machineIds, start, end, calendar, expenseCalculationId, includeTotals = true, windows,
  }, forceFirstPage = false) => {
    calendar = normalizeLiveSalesCalendar(calendar);
    const ids = Array.from(new Set(machineIds.filter(Boolean))).sort();
    const queryKey = liveSalesTransactionsKey({
      machineIds: ids, start, end, calendar, expenseCalculationId, includeTotals, windows,
    });
    const current = get().transactionQueries[queryKey];
    const replaceFirstPage = forceFirstPage || Boolean(current?.requiresFirstPage) || Boolean(
      current && windows?.some((window) => current.windows?.find((entry) => entry.machineId === window.machineId)?.end !== window.end),
    );
    if (current && !current.hasMore && !replaceFirstPage) return;
    const existing = transactionRequests.get(queryKey);
    if (existing) {
      try {
        await existing;
      } catch (error) {
        if (!replaceFirstPage) throw error;
      }
      if (transactionRequests.get(queryKey) === existing) {
        transactionRequests.delete(queryKey);
      }
      if (replaceFirstPage) {
        return get().loadTransactions({ machineIds: ids, start, end, calendar, expenseCalculationId, includeTotals, windows }, true);
      }
      return;
    }
    const generation = storeGeneration;
    const attempt = (current?.attempt || 0) + 1;
    // Register every attempt before starting I/O. Existing successful data stays
    // ready while a refresh runs; an initial request has a finite loading state.
    set((state) => ({
      transactionQueries: {
        ...state.transactionQueries,
        [queryKey]: {
          machineIds: ids,
          start,
          end,
          saleIds: current?.saleIds || [],
          nextCursor: replaceFirstPage ? null : current?.nextCursor || null,
          hasMore: true,
          ready: Boolean(current?.ready),
          totals: current?.totals || {
              cashSalesCents: 0,
              cardSalesCents: 0,
              coinSalesCents: 0,
              count: 0,
              cardCount: 0,
              cashCount: 0,
              cardSurchargeCents: 0,
              cardFeeBaseCents: 0,
          },
          breakdowns: current?.breakdowns || [],
          previousGrossCents: current?.previousGrossCents ?? null,
          previousPeriods: current?.previousPeriods,
          calendar,
          expenseCalculationId,
          includeTotals,
          windows,
          requiresFirstPage: replaceFirstPage || current?.requiresFirstPage,
          error: null,
          attempt,
          status: current?.ready ? "refreshing" : "loading",
          updatedAt: current?.updatedAt ?? null,
        },
      },
    }));
    const controller = new AbortController();
    transactionControllers.add(controller);
    const timeout = window.setTimeout(
      () => controller.abort(),
      TRANSACTION_REQUEST_TIMEOUT_MS,
    );
    const perfRange = liveSalesPerfRangeLabel(calendar);
    const perfPage = replaceFirstPage || !current?.ready ? "initial" : "more";
    const request = measureLiveSalesStep(
      `load transactions API ${perfRange} ${perfPage}`,
      () => fetchMachineSalesTransactionPage({
        machineIds: ids,
        windows,
        start,
        end,
        limit: TRANSACTION_PAGE_SIZE,
        cursor: replaceFirstPage ? null : current?.nextCursor,
        includeTotals: includeTotals && (replaceFirstPage || !current?.ready),
        calendarRange: calendar?.range,
        startDate: calendar?.startDate,
        endDate: calendar?.endDate,
        locationIds: calendar?.locationIds,
        expenseCalculationId,
        signal: controller.signal,
      }),
    ).then((page) => {
      logLiveSalesResourceTiming(
        `load transactions ${perfRange} ${perfPage}`,
        "/machine/sale/transactions",
      );
      if (generation !== storeGeneration) return;
      measureLiveSalesSync(
        `load transactions merge ${perfRange} ${perfPage}`,
        () => set((state) => {
        const salesById = { ...state.salesById };
        page.items.forEach((sale) => {
          if (sale.id) salesById[sale.id] = sale;
        });
        const pageSaleIds = page.items
          .map((sale) => sale.id)
          .filter((id): id is string => Boolean(id));
        const saleIds = replaceFirstPage
          ? Array.from(new Set(pageSaleIds))
          : Array.from(new Set([
              ...(state.transactionQueries[queryKey]?.saleIds || []),
              ...pageSaleIds,
            ]));
        return {
          salesById,
          transactionQueries: {
            ...state.transactionQueries,
            [queryKey]: {
              machineIds: ids,
              start,
              end,
              saleIds,
              nextCursor: page.nextCursor,
              // A next cursor is required to make forward progress. Treat an
              // inconsistent response as complete instead of requesting page
              // one forever.
              hasMore: Boolean(page.hasMore && page.nextCursor),
              ready: true,
              totals:
                replaceFirstPage || !current?.ready
                  ? page.totals
                  : current.totals,
              breakdowns: replaceFirstPage || !current?.ready
                ? page.breakdowns
                : current.breakdowns,
              previousPeriods: replaceFirstPage || !current?.ready
                ? page.previousPeriods
                : current.previousPeriods,
              previousGrossCents: replaceFirstPage || !current?.ready
                ? page.previousGrossCents
                : current.previousGrossCents,
              calendar,
              expenseCalculationId,
              includeTotals,
              windows,
              requiresFirstPage: false,
              error: null,
              attempt,
              status: "ready",
              updatedAt: Date.now(),
            },
          },
        };
        }),
      );
    }).catch((error) => {
      if (generation === storeGeneration) set((state) => ({ transactionQueries: {
        ...state.transactionQueries,
        [queryKey]: {
          ...state.transactionQueries[queryKey],
          // Keep committed rows visible if a background refresh fails.
          ready: Boolean(state.transactionQueries[queryKey]?.ready),
          hasMore: true,
          error: String(error),
          status: state.transactionQueries[queryKey]?.ready ? "ready" : "error",
        },
      }}));
      throw error;
    });
    transactionRequests.set(queryKey, request);
    try {
      await request;
    } finally {
      window.clearTimeout(timeout);
      transactionControllers.delete(controller);
      if (transactionRequests.get(queryKey) === request) transactionRequests.delete(queryKey);
    }
  },

  loadCollectionSummaries: async (machineIds) => {
    const ids = Array.from(new Set(machineIds.filter(Boolean))).sort();
    const current = get();
    const missingIds = ids.filter(
      (id) =>
        !current.collectionSummaries[id] &&
        !current.collectionSummaryStatus[id]?.loading
    );
    if (!missingIds.length) return;
    const requestKey = missingIds.join("|");
    const existing = collectionSummaryRequests.get(requestKey);
    if (existing) {
      await existing;
      if (collectionSummaryRequests.get(requestKey) === existing) {
        collectionSummaryRequests.delete(requestKey);
      }
      const stillMissing = missingIds.filter(
        (id) => !get().collectionSummaries[id]
      );
      if (stillMissing.length) {
        return get().loadCollectionSummaries(stillMissing);
      }
      return;
    }
    const generation = storeGeneration;
    const controller = new AbortController();
    collectionSummaryControllers.add(controller);
    const timeout = window.setTimeout(
      () => controller.abort(),
      COLLECTION_SUMMARY_TIMEOUT_MS
    );
    set((state) => {
      const collectionSummaryStatus = { ...state.collectionSummaryStatus };
      missingIds.forEach((id) => {
        collectionSummaryStatus[id] = {
          loading: true,
          error: null,
          attempt: collectionSummaryStatus[id]?.attempt || 0,
        };
      });
      return { collectionSummaryStatus };
    });
    const request = fetchMachineCollectionSummaryBulk(
      missingIds,
      controller.signal
    )
      .then((summaries) => {
        if (generation !== storeGeneration) return;
        set((state) => {
          const collectionSummaryStatus = {
            ...state.collectionSummaryStatus,
          };
          missingIds.forEach((id) => {
            const missing = !summaries[id];
            collectionSummaryStatus[id] = {
              loading: false,
              error: missing ? "Collection summary unavailable" : null,
              attempt: missing ? (collectionSummaryStatus[id]?.attempt || 0) + 1 : 0,
            };
          });
          return {
            collectionSummaries: {
              ...state.collectionSummaries,
              ...summaries,
            },
            collectionSummaryStatus,
          };
        });
      })
      .catch((error) => {
        if (generation !== storeGeneration) return;
        const message =
          error instanceof DOMException && error.name === "AbortError"
            ? "Collection summary request timed out"
            : error instanceof Error
              ? error.message
              : "Unable to load collection summaries";
        set((state) => {
          const collectionSummaryStatus = {
            ...state.collectionSummaryStatus,
          };
          missingIds.forEach((id) => {
            const previous = collectionSummaryStatus[id];
            collectionSummaryStatus[id] = {
              loading: false,
              error: message,
              attempt: (previous?.attempt || 0) + 1,
            };
          });
          return { collectionSummaryStatus };
        });
        throw error;
      });
    collectionSummaryRequests.set(requestKey, request);
    try {
      await request;
    } finally {
      window.clearTimeout(timeout);
      collectionSummaryControllers.delete(controller);
      if (collectionSummaryRequests.get(requestKey) === request) {
        collectionSummaryRequests.delete(requestKey);
      }
    }
  },

  invalidateCollectionSummaries: (machineIds) => {
    if (!machineIds) {
      set({ collectionSummaries: {} });
      return;
    }
    set((state) => {
      const collectionSummaries = { ...state.collectionSummaries };
      machineIds.forEach((id) => delete collectionSummaries[id]);
      return { collectionSummaries };
    });
  },

  reset: () => {
    storeGeneration += 1;
    synchronization = null;
    queuedMachineIds.clear();
    activeSynchronizationMachineIds.clear();
    summaryRequests.clear();
    transactionRequests.clear();
    transactionControllers.forEach((controller) => controller.abort());
    transactionControllers.clear();
    collectionSummaryControllers.forEach((controller) => controller.abort());
    collectionSummaryControllers.clear();
    collectionSummaryRequests.clear();
    activeSummaryQueries.clear();
    activeTransactionQueries.clear();
    set({
      salesById: {},
      dailyBuckets: {},
      machineVersions: {},
      collectionSummaries: {},
      collectionSummaryStatus: {},
      summaryQueries: {},
      transactionQueries: {},
    });
  },
}));

const refreshLiveSalesQueries = async (
  summaries: [string, LiveSalesSummaryQuery][],
  transactions: [string, LiveSalesTransactionQuery][],
) => {
  if (!summaries.length && !transactions.length) return;
  useLiveSalesStore.setState((state) => {
    const summaryQueries = { ...state.summaryQueries };
    const transactionQueries = { ...state.transactionQueries };
    summaries.forEach(([key, query]) => {
      summaryQueries[key] = {
        ...query,
        ready: activeSummaryQueries.has(key) && query.ready,
        error: null,
      };
    });
    transactions.forEach(([key, query]) => {
      const ready = activeTransactionQueries.has(key) && query.ready;
      transactionQueries[key] = {
        ...query,
        nextCursor: null,
        hasMore: true,
        requiresFirstPage: true,
        ready,
        error: null,
        status: ready ? "refreshing" : "idle",
      };
    });
    return { summaryQueries, transactionQueries };
  });
  // Keep committed values visible while active views refresh. Dormant views
  // fetch a new first page when reopened, without appending stale rows.
  await Promise.all([
    ...summaries.filter(([key]) => activeSummaryQueries.has(key)).map(([, query]) =>
      useLiveSalesStore.getState().loadSummary(query.machineIds, query.start, query.end, query.calendar, true),
    ),
    ...transactions.filter(([key]) => activeTransactionQueries.has(key)).map(([, query]) =>
      useLiveSalesStore.getState().loadTransactions(query, true),
    ),
  ]);
};

export const liveSalesBucketKey = bucketKey;

export const resetMachineCollectionSummaryCache = (machineIds?: string[]) =>
  useLiveSalesStore.getState().invalidateCollectionSummaries(machineIds);

export const selectLiveSalesSummary = (
  state: LiveSalesState,
  machineIds: string[],
  start: string,
  end: string,
  locationIds?: string[]
) => (state.summaryQueries[liveSalesSummaryKey(machineIds, start, end)]?.bucketKeys || []).reduce(
  (total, key) => {
    const bucket = state.dailyBuckets[key];
    if (!bucket || (locationIds?.length && !locationIds.includes(bucket.location || ""))) {
      return total;
    }
    total.cashSalesCents += bucket.cashSalesCents;
    total.cardSalesCents += bucket.cardSalesCents;
    total.coinSalesCents += bucket.coinSalesCents;
    total.count += bucket.count;
    total.cardCount += bucket.cardCount;
    total.cashCount += bucket.cashCount;
    total.grossSalesCents += bucket.cashSalesCents + bucket.cardSalesCents + bucket.coinSalesCents;
    total.commissionCents += bucket.commissionCents ?? 0;
    total.commissionCompleteCount += bucket.commissionCompleteCount ?? 0;
    total.commissionNotApplicableCount += bucket.commissionNotApplicableCount ?? 0;
    return total;
  },
  { cashSalesCents: 0, cardSalesCents: 0, coinSalesCents: 0, count: 0, cardCount: 0, cashCount: 0, grossSalesCents: 0, commissionCents: 0, commissionCompleteCount: 0, commissionNotApplicableCount: 0 }
);
